import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import moment from 'moment';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';

import { GET_ROUTE_FOOD_PARTNER_ORDER, GET_ROUTE_FOOD_PARTNER_ORDER_EDIT, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../core/constants';
import { getFoodPartnerOrdersState } from '../../../../features/inventory/get_food_partner_orders';
import { getFoodPartnerOrderStatus, getFoodPartnerOrderStatusColor } from '../../../common/utils/food_partner_order_utils';
import { CustomIconButton, DeleteButton } from '../../../common/button';
import { generateFoodPartnerOrderLabelsNotificationData, sendFoodPartnerOrderNotificationData, updateFoodPartnerOrderStatusNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import useNotification from '../../../../core/hooks/use_notification';
import { updateFoodPartnerOrderStatusAction } from '../../../../features/inventory/update_food_partner_order_status';
import { FoodPartnerOrderStatusEnum } from '../../../../core/enums/inventory/food_partner_order';
import { sendFoodPartnerOrderAction } from '../../../../features/inventory/send_food_partner_order';
import { generateFoodPartnerOrderLabelsAction } from '../../../../features/inventory/generate_food_partner_order_labels';
import { FormRouteEnum } from '../../../../core/enums/common/route';
import ConfirmDialog from '../../common/confirm_dialog';

const FoodPartnerOrdersList = ({ refreshList }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    const getFoodPartnerOrders = useSelector(getFoodPartnerOrdersState);

    const [loadingEmailIds, setLoadingEmailIds] = useState([]);
    const [loadingLabelIds, setLoadingLabelIds] = useState([]);
    const [loadingCancelIds, setLoadingCancelIds] = useState([]);

    const onSendFoodPartnerOrderEmail = (foodPartnerOrderId, generateLabels) => {
        setLoadingEmailIds(prev => [...prev, foodPartnerOrderId]);

        dispatch(sendFoodPartnerOrderAction({ id: foodPartnerOrderId, generateLabels }))
            .then(response => {
                showNotification(sendFoodPartnerOrderNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setLoadingEmailIds(prev => prev.filter(id => id !== foodPartnerOrderId));
            });
    };

    const onGenerateFoodPartnerOrderLabels = (foodPartnerOrderId) => {
        setLoadingLabelIds(prev => [...prev, foodPartnerOrderId]);

        dispatch(generateFoodPartnerOrderLabelsAction({ id: foodPartnerOrderId }))
            .then(response => {
                showNotification(generateFoodPartnerOrderLabelsNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setLoadingLabelIds(prev => prev.filter(id => id !== foodPartnerOrderId));
            });
    };

    const onCancelFoodPartnerOrder = (foodPartnerOrderId) => {
        setLoadingCancelIds(prev => [...prev, foodPartnerOrderId]);

        dispatch(updateFoodPartnerOrderStatusAction({
            id: foodPartnerOrderId,
            status: FoodPartnerOrderStatusEnum.CANCELLED.value
        }))
            .then(response => {
                showNotification(updateFoodPartnerOrderStatusNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshList();
            })
            .finally(() => {
                setLoadingCancelIds(prev => prev.filter(id => id !== foodPartnerOrderId));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Tytuł</TableCell>
                        <TableCell align="center">Data utworzenia</TableCell>
                        <TableCell align="center">Data zamówienia</TableCell>
                        <TableCell align="center">Wysłano email</TableCell>
                        <TableCell align="center">Podział na lokalizacje</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right" sx={ { minWidth: 200 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { getFoodPartnerOrders?.data?.content?.map(foodPartnerOrder => (
                        <TableRow key={ foodPartnerOrder?.id } hover>
                            <TableCell sx={ { fontWeight: 500 } }>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gap={ 1 }>
                                    { foodPartnerOrder?.title }
                                    { foodPartnerOrder?.purposeType === 'RESERVATION' && (
                                        <Chip
                                            title="Rezerwacje użytkowników"
                                            label="R"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                        />
                                    ) }
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                { moment(foodPartnerOrder?.createDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                            </TableCell>
                            <TableCell align="center">
                                { moment(foodPartnerOrder?.orderDate, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={ foodPartnerOrder?.emailStatus !== 'UNSENT' ? "TAK" : "NIE" }
                                    color={ foodPartnerOrder?.emailStatus !== 'UNSENT' ? "success" : "default" }
                                    size="small"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={ foodPartnerOrder?.locationDivision ? "TAK" : "NIE" }
                                    color={ foodPartnerOrder?.locationDivision ? "success" : "default" }
                                    size="small"
                                    variant="outlined"
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={ getFoodPartnerOrderStatus(foodPartnerOrder?.status) }
                                    color={ getFoodPartnerOrderStatusColor(foodPartnerOrder?.status) }
                                    size="small"
                                />
                            </TableCell>
                            <TableCell align="right">
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={ 1 }>
                                    { ([FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value].includes(foodPartnerOrder?.status)) && (
                                        <ConfirmDialog
                                            isLoading={ loadingEmailIds.includes(foodPartnerOrder?.id) }
                                            maxWidth="md"
                                            title={ foodPartnerOrder?.emailStatus !== 'UNSENT' ?
                                                'Czy na pewno chcesz ponownie wysłać maila do Dostawcy? Pamiętaj, żeby wysyłać maila tylko przy aktualizacji zamówienia. Możesz uwzględnić paczkę z naklejkami w mailu z aktualizacją, lub wysłać ją bez naklejek.' :
                                                'Czy chcesz uwzględnić paczkę z naklejkami w mailu z zamówieniem?' }
                                            cancelText="Anuluj"
                                            secondaryAction={ () => onSendFoodPartnerOrderEmail(foodPartnerOrder?.id, false) }
                                            secondaryText="Wyślij bez naklejek"
                                            primaryAction={ () => onSendFoodPartnerOrderEmail(foodPartnerOrder?.id, true) }
                                            primaryText="Wyślij z naklejkami"
                                            displayIcon
                                            iconTitle="Wyślij email"
                                            icon={ <EmailIcon fontSize="small"/> }
                                        />
                                    ) }

                                    <CustomIconButton
                                        title="Pobierz naklejki"
                                        isLoading={ loadingLabelIds.includes(foodPartnerOrder?.id) }
                                        onClick={ () => onGenerateFoodPartnerOrderLabels(foodPartnerOrder?.id) }
                                        icon={ <DownloadIcon fontSize="small"/> }
                                    />

                                    { ([FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value, FoodPartnerOrderStatusEnum.DELIVERED.value].includes(foodPartnerOrder?.status)) ? (
                                        <Tooltip title="Edytuj">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={ () => navigate(GET_ROUTE_FOOD_PARTNER_ORDER_EDIT(foodPartnerOrder?.id), { state: { action: FormRouteEnum.EDIT } }) }
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Podgląd">
                                            <IconButton
                                                size="small"
                                                onClick={ () => navigate(GET_ROUTE_FOOD_PARTNER_ORDER(foodPartnerOrder?.id), { state: { action: FormRouteEnum.VIEW } }) }
                                            >
                                                <SearchIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    ) }

                                    { [FoodPartnerOrderStatusEnum.CREATED.value, FoodPartnerOrderStatusEnum.IN_PROGRESS.value, FoodPartnerOrderStatusEnum.DELIVERED.value].includes(foodPartnerOrder?.status) && (
                                        <Box ml={ 0.5 }>
                                            <DeleteButton
                                                onDeleteClick={ () => onCancelFoodPartnerOrder(foodPartnerOrder?.id) }
                                                isLoading={ loadingCancelIds.includes(foodPartnerOrder?.id) }
                                            />
                                        </Box>
                                    ) }
                                </Stack>
                            </TableCell>
                        </TableRow>
                    )) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default FoodPartnerOrdersList;